/**
 * Pure utility functions for tile coordinate math and date windowing.
 *
 * Extracted from TileCompositeService so they can be tested in isolation
 * and reused by other tile-related modules.
 */

/**
 * Generate an array of ISO date strings (YYYY-MM-DD) ending at `endDateStr`
 * and spanning `windowDays` days backwards. The array is ordered newest-first.
 */
export function getCompositeDates(endDateStr: string, windowDays: number): string[] {
  const dates: string[] = []
  const end = new Date(endDateStr + 'T00:00:00Z')
  for (let i = 0; i < windowDays; i++) {
    const d = new Date(end)
    d.setUTCDate(d.getUTCDate() - i)
    dates.push(d.toISOString().split('T')[0])
  }
  return dates // newest first
}

/**
 * Convert a lat/lng bbox to the inclusive tile range that covers it at the
 * given zoom. Uses standard Web Mercator tile math.
 *
 * @param bbox - [west, south, east, north] in lng/lat degrees
 * @returns Inclusive tile index range at the given zoom level.
 */
export function bboxToTileRange(
  bbox: [number, number, number, number],
  z: number
): { minX: number; minY: number; maxX: number; maxY: number } {
  const [west, south, east, north] = bbox
  const n = Math.pow(2, z)
  const maxTile = n - 1

  // X: straightforward linear mapping of longitude
  const minX = Math.floor(((west + 180) / 360) * n)
  const maxX = Math.floor(((east + 180) / 360) * n)

  // Y: Web Mercator, north has the lower Y index
  const latToTileY = (lat: number): number => {
    const clamped = Math.max(-85.05112878, Math.min(85.05112878, lat))
    const rad = (clamped * Math.PI) / 180
    return Math.floor(((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * n)
  }
  const minY = latToTileY(north)
  const maxY = latToTileY(south)

  return {
    minX: Math.max(0, Math.min(maxTile, minX)),
    minY: Math.max(0, Math.min(maxTile, minY)),
    maxX: Math.max(0, Math.min(maxTile, maxX)),
    maxY: Math.max(0, Math.min(maxTile, maxY)),
  }
}

/**
 * Find the lowest zoom level Z* at which the given bbox is covered by at most
 * a 2×2 tile grid. This is the "snap-to-tile-aligned extent" floor used by the
 * plan download workflow.
 */
export function findFloorZoom(bbox: [number, number, number, number]): number {
  for (let z = 0; z <= 22; z++) {
    const { minX, minY, maxX, maxY } = bboxToTileRange(bbox, z)
    const w = maxX - minX + 1
    const h = maxY - minY + 1
    if (w <= 2 && h <= 2) continue // still small enough — try deeper
    return Math.max(0, z - 1) // previous zoom was the last 2×2 fit
  }
  return 22
}

/**
 * Convert tile coordinates (z/x/y) to a Web Mercator (EPSG:3857) BBOX string
 * of the form "xMin,yMin,xMax,yMax".
 */
export function tileBbox3857(z: number, x: number, y: number): string {
  const n = Math.pow(2, z)
  const HALF_WORLD = 20037508.342789244
  const worldSize = HALF_WORLD * 2
  const tileSize = worldSize / n
  const xMin = x * tileSize - HALF_WORLD
  const xMax = (x + 1) * tileSize - HALF_WORLD
  const yMin = HALF_WORLD - (y + 1) * tileSize
  const yMax = HALF_WORLD - y * tileSize
  return `${xMin},${yMin},${xMax},${yMax}`
}

/**
 * Clamp a date so it is no more recent than `latencyDays` days before now.
 * Returns an ISO date string (YYYY-MM-DD).
 */
export function computeEndDate(time: Date, latencyDays: number): string {
  const clamped = new Date(time)
  const cutoff = new Date()
  cutoff.setDate(cutoff.getDate() - latencyDays)
  if (clamped > cutoff) {
    clamped.setTime(cutoff.getTime())
  }
  return clamped.toISOString().split('T')[0]
}
